import { useState, useMemo, useEffect, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Loading } from '@evertrue/et-components';
import EverTrue from 'app';
import UserStore from 'base/user/user-store';
import { fetchUsersById } from 'apps/volunteers/vms-queries';
import CadenceDetailBreadcrumb from './cadence-detail-breadcrumb';
import DuplicateCadenceModal from '../duplicate/duplicate-cadence-modal';
import CadenceSharingModal from '../sharing/cadence-sharing-modal';
import ConfirmModal from '../shared/confirm-modal';
import useCadence from '../../hooks/use-cadence';
import useTemplateInstances from '../../hooks/use-template-instances';
import useDetailPageActions from '../../hooks/use-detail-page-actions';
import useCadenceMutations from '../../hooks/use-cadence-mutations';
import { formatDateOrDash, METRIC_TYPE_LABELS, getRoundedPercentage } from '../../utils/cadence-data-mappers';
import cadenceGqlCall from '../../utils/cadence-gql-call';
import { CADENCE_STATS_QUERY } from '../../utils/cadence-queries';
import {
  walkCadenceGraph,
  ACTION_TYPE_INFO,
  getBranchActionSteps,
  getBranchStepLabelList,
} from '../../utils/cadence-step-utils';
import { isBulkMessageType } from '../../utils/cadence-enums';
import Api from 'entities/helpers/api';
import EmailTemplateFormModal from 'apps/outreach/email/templates/components/email-template-form-modal';
import ErrorLogger from 'entities/helpers/error-log-helper';

/** Per-metric sublabel text for the blueprint success stat card. */
const METRIC_SUBLABELS = {
  interaction_created:
    'of recipients in cadences tied to this blueprint went on to have the targeted interaction type created',
  gift_given: 'of recipients in cadences tied to this blueprint went on to give the targeted gift',
  email_opened: 'of recipients in cadences tied to this blueprint opened an email',
  email_replied: 'of recipients in cadences tied to this blueprint replied to an email',
  email_clicked: 'of recipients in cadences tied to this blueprint clicked an email link',
  portfolio_stage_reached: 'of recipients in cadences tied to this blueprint reached the target portfolio stage',
  saved_search: 'of recipients in cadences tied to this blueprint qualified for the saved search',
  proposal_funded: 'of recipients in cadences tied to this blueprint had a proposal funded',
  gifts_amount: 'of recipients in cadences tied to this blueprint gave gifts over the target amount',
  gifts_count: 'of recipients in cadences tied to this blueprint reached the target number of gifts',
  meetings_count: 'of recipients in cadences tied to this blueprint reached the target number of meetings',
};

/** Get unique step type labels for the stats card preview text. */
const getStepTypePreview = (events = []) => {
  const types = new Set();
  (events || []).forEach((e) => {
    const t = (e.eventType || '').toLowerCase();
    if (['start', 'end', 'wait', 'condition'].includes(t)) return;
    types.add(ACTION_TYPE_INFO[t]?.label || e.eventType);
  });
  return [...types].join(' \u00b7 ');
};

/** Delegate graph walk to shared utility — buildBlueprintSteps is now walkCadenceGraph. */
const buildBlueprintSteps = walkCadenceGraph;

const STATUS_STYLES = {
  active: { className: 'is-active', label: 'Active' },
  paused: { className: 'is-paused', label: 'Paused' },
  archived: { className: 'is-archived', label: 'Archived' },
  complete: { className: 'is-archived', label: 'Complete' },
  draft: { className: 'is-draft', label: 'Draft' },
};

/**
 * Blueprint detail page (spec 11).
 * Shows blueprint metadata, 3 stat cards, details + cadences using, and step table with branching.
 */
const TemplateDetailPage = ({ templateId }) => {
  const { cadence: template, loading, error, refetch } = useCadence(templateId);
  const { instances: cadencesUsingTemplate } = useTemplateInstances(templateId);
  const { deleteCadence, createCadenceFromTemplate } = useCadenceMutations();
  const actions = useDetailPageActions({
    entityId: templateId,
    entity: template ? { name: template.name } : null,
    rawEntity: template,
    refetch,
    entityType: 'template',
  });

  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState(null);
  const [namePrompt, setNamePrompt] = useState(null);
  const [namePromptValue, setNamePromptValue] = useState('');
  const [expandedBranches, setExpandedBranches] = useState({});
  const [emailTemplateModal, setEmailTemplateModal] = useState({ isOpen: false, templateData: null });
  const overflowRef = useRef(null);

  const currentUserId = UserStore.getUser()?.id;
  // Share is truly owner-only — canDelete can be granted via sharing
  const isCadenceOwner = currentUserId != null && String(template?.ownerUserId) === String(currentUserId);

  // Close overflow on outside click
  useEffect(() => {
    if (!showOverflowMenu) return;
    const handleClick = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) {
        setShowOverflowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showOverflowMenu]);

  const {
    items: stepItems,
    actionableCount,
    conditionCount,
    hasAutomated,
    hasManual,
  } = useMemo(() => buildBlueprintSteps(template?.events || [], template?.edges || []), [template]);

  const stepTypePreview = useMemo(() => getStepTypePreview(template?.events), [template]);

  const goalMetricName = useMemo(() => {
    const metrics = template?.successMetrics;
    if (!metrics?.length) return null;
    const m = metrics[0];
    return METRIC_TYPE_LABELS[m.metricType] || m.metricType;
  }, [template]);

  const goalLabel = useMemo(() => {
    const metrics = template?.successMetrics;
    if (!metrics?.length) return null;
    const m = metrics[0];
    return m.targetPercentage ? `${goalMetricName} (${m.targetPercentage}%)` : goalMetricName;
  }, [template, goalMetricName]);

  const isBulkBlueprint = isBulkMessageType(template?.cadenceType);
  const typeNoun = isBulkBlueprint ? 'Bulk Messages' : 'Cadences';

  /** Build detailed goal sub-info based on metric config. */
  const goalDetails = useMemo(() => {
    const metrics = template?.successMetrics;
    if (!metrics?.length) return null;
    const m = metrics[0];
    const cfg = m.metricConfig || {};
    const details = [];
    switch (m.metricType) {
      case 'interaction_created':
        if (cfg.interaction_type)
          details.push({ label: 'Interaction Type', value: cfg.interaction_type.replace(/_/g, ' ') });
        break;
      case 'gift_given':
        if (cfg.min_amount) details.push({ label: 'Min Amount', value: `$${Number(cfg.min_amount).toLocaleString()}` });
        if (cfg.designation) details.push({ label: 'Designation', value: cfg.designation });
        break;
      case 'portfolio_stage_reached':
        if (cfg.team_name) details.push({ label: 'Team', value: cfg.team_name });
        if (cfg.stage_name) details.push({ label: 'Stage', value: cfg.stage_name });
        break;
      case 'saved_search':
        if (cfg.saved_search_name) details.push({ label: 'Saved Search', value: cfg.saved_search_name });
        break;
      case 'gifts_amount':
        if (cfg.threshold) details.push({ label: 'Min Total', value: `$${Number(cfg.threshold).toLocaleString()}` });
        break;
      case 'gifts_count':
        if (cfg.threshold) details.push({ label: 'Min Count', value: String(cfg.threshold) });
        break;
      case 'meetings_count':
        if (cfg.threshold) details.push({ label: 'Min Count', value: String(cfg.threshold) });
        break;
      default:
        break;
    }
    return details.length > 0 ? details : null;
  }, [template]);

  /** Aggregate stats from all child cadences of this blueprint. */
  const [aggregateStats, setAggregateStats] = useState(null);
  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;
    cadenceGqlCall(CADENCE_STATS_QUERY, { cadenceId: String(templateId) })
      .then((res) => {
        if (!cancelled) setAggregateStats(res?.cadenceStats || null);
      })
      .catch(() => {
        if (!cancelled) setAggregateStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  const avgSuccess = useMemo(() => {
    const pct = getRoundedPercentage(aggregateStats?.qualifiedMembers, aggregateStats?.totalMembers);
    return pct === null ? null : `${pct}%`;
  }, [aggregateStats]);

  const typeLabel = useMemo(() => {
    const ct = template?.cadenceType;
    if (ct === 'prospect_plan') return 'Prospect Plan';
    if (ct === 'bulk_message') return 'Bulk Message';
    return 'Cadence';
  }, [template]);

  const [createdByLabel, setCreatedByLabel] = useState('\u2014');

  useEffect(() => {
    const userId = template?.createdByUserId || template?.ownerUserId;
    if (!userId) {
      setCreatedByLabel('\u2014');
      return;
    }
    if (String(userId) === String(currentUserId)) {
      setCreatedByLabel('You');
      return;
    }
    setCreatedByLabel(`User #${userId}`);
    fetchUsersById([userId])
      .then((users) => {
        const user = users?.[0];
        if (user?.name && user.name !== 'Removed User') {
          setCreatedByLabel(user.name);
        }
      })
      .catch((e) => console.warn('[cadences] background fetch failed:', e.message));
  }, [template, currentUserId]);

  // Initialize all branches as expanded
  useEffect(() => {
    const expanded = {};
    stepItems.forEach((item, i) => {
      if (item.type === 'branch') {
        expanded[`${i}_true`] = true;
        expanded[`${i}_false`] = true;
      }
    });
    setExpandedBranches(expanded);
  }, [stepItems]);

  const toggleBranch = (key) => {
    setExpandedBranches((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleTemplateClick = async (emailTemplateId) => {
    if (!emailTemplateId) return;
    try {
      const templateData = await Api.OUTREACH.EMAIL_TEMPLATE.get({ urlArgs: { id: emailTemplateId } });
      setEmailTemplateModal({ isOpen: true, templateData });
    } catch (err) {
      ErrorLogger.error('Error fetching email template', { extra: { err, emailTemplateId } });
      EverTrue.Alert.error('Could not load email template');
    }
  };

  /** Save handler for the email template modal — calls the API directly. */
  const handleTemplateSave = async (values) => {
    try {
      await Api.OUTREACH.EMAIL_TEMPLATE.put({
        urlArgs: { id: values.id },
        data: JSON.stringify({
          templateName: values.templateName,
          templateSubject: values.templateSubject,
          templateBody: values.templateBody,
          dynamicTemplate: values.dynamicTemplate,
          tags: values.tags,
          oid: values.oid,
          userId: values.userId,
        }),
      });
      EverTrue.Alert.success('Email template updated');
    } catch (err) {
      ErrorLogger.error('Error updating email template', { extra: { err, templateId: values.id } });
      EverTrue.Alert.error('Could not update email template');
    }
  };

  const handleBack = () => {
    EverTrue.Navigator('/cadences-v2?tab=templates', true);
  };

  const handleViewSetup = () => {
    const path = isBulkBlueprint ? `/cadences-v2/bulk-message/${templateId}/edit` : `/cadences-v2/${templateId}/edit`;
    EverTrue.Navigator(path, true);
  };

  const handleUseForNewCadence = () => {
    setNamePromptValue('');
    setNamePrompt(true);
  };

  const handleNamePromptSubmit = async () => {
    const name = namePromptValue.trim();
    if (!name) return;
    setNamePrompt(null);
    try {
      const cadenceType = template?.cadenceType;
      const result = await createCadenceFromTemplate(templateId, {
        name,
        status: 'draft',
        ...(cadenceType ? { cadenceType } : {}),
      });
      EverTrue.Alert.success('Cadence created from blueprint');
      if (result?.cadence?.id) {
        const isBulk = isBulkMessageType(result.cadence.cadenceType || cadenceType);
        const route = isBulk
          ? `/cadences-v2/bulk-message/${result.cadence.id}/edit`
          : `/cadences-v2/${result.cadence.id}`;
        EverTrue.Navigator(route, { trigger: true });
      }
    } catch (err) {
      EverTrue.Alert.error(`Could not create cadence: ${err.message}`);
    }
  };

  const handleDelete = () => {
    setShowOverflowMenu(false);
    setConfirmConfig({
      title: 'Delete Blueprint',
      body: 'This cannot be undone.',
      confirmLabel: 'Delete',
      isDanger: true,
      onConfirm: async () => {
        try {
          await deleteCadence(templateId, template.version);
          EverTrue.Alert.success('Blueprint deleted');
          handleBack();
        } catch (err) {
          EverTrue.Alert.error(`Delete failed: ${err.message}`);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="template-detail-page">
        <div className="template-detail-page--loading">
          <Loading />
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="template-detail-page">
        <div className="template-detail-page--error">
          <Icon icon="alert-circle" size={2} />
          <h3>{error ? 'Failed to load cadence blueprint' : 'Cadence blueprint not found'}</h3>
          {error && <p>{error}</p>}
          <Button type="secondary" onClick={handleBack}>
            Back to Blueprints
          </Button>
        </div>
      </div>
    );
  }

  if (!template.canView) {
    return (
      <div className="template-detail-page">
        <div className="template-detail-page--error">
          <Icon icon="lock" size={2} />
          <h3>You don&apos;t have access to this blueprint</h3>
          <p>This blueprint has not been shared with you.</p>
          <Button type="secondary" onClick={handleBack}>
            Back to Blueprints
          </Button>
        </div>
      </div>
    );
  }

  const description = template.description
    ? `${actionableCount}-step ${typeLabel.toLowerCase()} blueprint for ${template.description}`
    : `${actionableCount}-step ${typeLabel.toLowerCase()} blueprint`;

  const actionTypeClass = (actionType) => {
    const t = (actionType || '').toLowerCase();
    if (t === 'email') return 'is-email';
    if (t === 'call' || t === 'phone') return 'is-call';
    if (t === 'text' || t === 'sms') return 'is-text';
    if (t === 'in-person') return 'is-in-person';
    if (t === 'manual') return 'is-manual';
    return 'is-other';
  };

  const renderStepRow = (step) => (
    <tr
      key={step.id}
      className={step.condBadgeType ? `template-detail-page--branch-step is-${step.condBadgeType}` : ''}
    >
      <td>
        <div className="template-detail-page--step-num-group">
          <span className="template-detail-page--step-num">{step.event?.stepLabel || `${step.num}${step.suffix}`}</span>
          {step.condBadge && (
            <span className={`template-detail-page--cond-badge is-${step.condBadgeType}`}>{step.condBadge}</span>
          )}
        </div>
      </td>
      <td>
        <span className="template-detail-page--step-name">{step.name}</span>
      </td>
      <td>
        <span className={`template-detail-page--action-pill ${actionTypeClass(step.actionType)}`}>
          <Icon icon={step.actionIcon} />
          {step.actionType}
        </span>
      </td>
      <td>
        <span
          className={`template-detail-page--mode-badge ${step.mode === 'Automated' ? 'is-automated' : 'is-manual'}`}
        >
          {step.mode === 'Automated' ? (
            <span className="template-detail-page--mode-icon">⚡</span>
          ) : (
            <Icon icon="person" />
          )}
          {step.mode}
        </span>
      </td>
      <td className="template-detail-page--timing-cell">{step.timing}</td>
      <td>
        {step.templateName ? (
          step.emailTemplateId ? (
            <button
              type="button"
              className="template-detail-page--template-link"
              onClick={() => handleTemplateClick(step.emailTemplateId)}
            >
              {step.templateName}
            </button>
          ) : (
            <span className="template-detail-page--template-link">
              {step.templateName}
              <span className="template-detail-page--inline-badge">Inline</span>
            </span>
          )
        ) : (
          '\u2014'
        )}
      </td>
    </tr>
  );

  const renderStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    const style = STATUS_STYLES[s] || { className: '', label: status || '\u2014' };
    return <span className={`template-detail-page--status-badge ${style.className}`}>{style.label}</span>;
  };

  return (
    <div className="template-detail-page">
      <CadenceDetailBreadcrumb crumbs={[{ label: 'Blueprints', onClick: handleBack }]} currentName={template.name} />

      <div className="template-detail-page--header">
        <div className="template-detail-page--title-row">
          <div className="template-detail-page--title-info">
            {template.canEdit && actions.isEditingName ? (
              <input
                type="text"
                className="cadence-inline-title-input"
                value={actions.editName}
                onChange={(e) => actions.setEditName(e.target.value)}
                onBlur={actions.handleSubmitRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') actions.handleSubmitRename();
                  if (e.key === 'Escape') actions.handleCancelRename();
                }}
                autoFocus
              />
            ) : template.canEdit ? (
              <button type="button" className="cadence-inline-title-btn" onClick={actions.handleStartRename}>
                <h1 className="template-detail-page--title">{template.name}</h1>
                <Icon icon="edit" className="cadence-inline-title-edit-icon" />
              </button>
            ) : (
              <h1 className="template-detail-page--title">{template.name}</h1>
            )}
            <p className="template-detail-page--description">{description}</p>
          </div>
          <div className="template-detail-page--actions">
            <Button type="secondary" onClick={handleViewSetup}>
              <Icon icon={template.canEdit ? 'edit' : 'visibility'} />
              <span>View Setup</span>
            </Button>
            {isCadenceOwner && (
              <Button type="secondary" onClick={() => setShowShareModal(true)}>
                <Icon icon="share" />
                <span>Share</span>
              </Button>
            )}
            <Button type="action" onClick={handleUseForNewCadence}>
              <Icon icon="add" />
              <span>
                {isBulkMessageType(template?.cadenceType) ? 'Use for New Bulk Message' : 'Use for New Cadence'}
              </span>
            </Button>
            <div className="template-detail-page--overflow" ref={overflowRef}>
              <button
                type="button"
                className="template-detail-page--more-btn"
                onClick={() => setShowOverflowMenu((v) => !v)}
                aria-label="More actions"
              >
                <Icon icon="more-vert" />
              </button>
              {showOverflowMenu && (
                <div className="template-detail-page--overflow-menu" role="menu">
                  {template.canEdit && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        handleViewSetup();
                        setShowOverflowMenu(false);
                      }}
                    >
                      <Icon icon="edit" /> Edit
                    </button>
                  )}
                  {template.canEdit && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        actions.handleStartRename();
                        setShowOverflowMenu(false);
                      }}
                    >
                      <Icon icon="edit" /> Rename
                    </button>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      actions.handleDuplicate();
                      setShowOverflowMenu(false);
                    }}
                  >
                    <Icon icon="content-copy" iconKit="gt2" /> Duplicate
                  </button>
                  {template.canDelete && (
                    <button
                      type="button"
                      role="menuitem"
                      className="template-detail-page--overflow-danger"
                      onClick={handleDelete}
                    >
                      <Icon icon="delete" /> Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {namePrompt && (
        <div className="cadence-shell-name-bar">
          <label>New cadence name:</label>
          <input
            type="text"
            value={namePromptValue}
            onChange={(e) => setNamePromptValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleNamePromptSubmit();
              if (e.key === 'Escape') setNamePrompt(null);
            }}
            autoFocus
          />
          <Button type="action" onClick={handleNamePromptSubmit}>
            Create
          </Button>
          <Button type="simple" onClick={() => setNamePrompt(null)}>
            Cancel
          </Button>
        </div>
      )}

      {/* 3 Stat Cards */}
      <div className="template-detail-page--content">
        <div className="template-detail-page--stats">
          <div className="template-detail-page--stat-card">
            <span className="template-detail-page--stat-label">{typeNoun.toUpperCase()} CREATED</span>
            <span className="template-detail-page--stat-value">{cadencesUsingTemplate.length}</span>
            <span className="template-detail-page--stat-sublabel">{typeNoun} built from this blueprint</span>
          </div>
          <div className="template-detail-page--stat-card">
            <span className="template-detail-page--stat-label">
              {goalMetricName ? goalMetricName.toUpperCase() : 'SUCCESS'}
            </span>
            <span className="template-detail-page--stat-value">{avgSuccess || '\u2014'}</span>
            <span className="template-detail-page--stat-sublabel">
              {template?.successMetrics?.[0]?.metricType
                ? METRIC_SUBLABELS[template.successMetrics[0].metricType] ||
                  `of recipients in cadences tied to this blueprint reached this goal`
                : 'No goal set'}
            </span>
          </div>
          <div className="template-detail-page--stat-card">
            <span className="template-detail-page--stat-label">STEPS</span>
            <span className="template-detail-page--stat-value">{actionableCount}</span>
            <span className="template-detail-page--stat-sublabel">{stepTypePreview || 'No steps'}</span>
          </div>
        </div>

        {/* Two-column: Details + Cadences Using */}
        <div className="template-detail-page--main-content">
          <div className="template-detail-page--details-card">
            <h2 className="template-detail-page--card-title">Details</h2>
            <div className="template-detail-page--details-list">
              <div className="template-detail-page--details-row">
                <span className="template-detail-page--details-label">Type</span>
                <span className="template-detail-page--details-value">{typeLabel}</span>
              </div>
              <div className="template-detail-page--details-row">
                <span className="template-detail-page--details-label">Goal</span>
                <span className={`template-detail-page--details-value${goalDetails ? ' has-details' : ''}`}>
                  <span>{goalLabel || '\u2014'}</span>
                  {goalDetails && (
                    <span className="template-detail-page--goal-details">
                      {goalDetails.map((d) => (
                        <span key={d.label} className="template-detail-page--goal-detail-item">
                          {d.label}: {d.value}
                        </span>
                      ))}
                    </span>
                  )}
                </span>
              </div>
              <div className="template-detail-page--details-row">
                <span className="template-detail-page--details-label">Steps</span>
                <span className="template-detail-page--details-value">
                  {actionableCount}
                  {conditionCount > 0 && ` (${conditionCount + 1} paths)`}
                </span>
              </div>
              {template?.cadenceType !== 'bulk_message' && (
                <div className="template-detail-page--details-row">
                  <span className="template-detail-page--details-label">Conditional Logic</span>
                  <span className={`template-detail-page--details-value ${conditionCount > 0 ? 'is-yes' : ''}`}>
                    {conditionCount > 0 ? (
                      <>
                        <Icon icon="check-circle" /> Yes ({conditionCount} condition{conditionCount > 1 ? 's' : ''})
                      </>
                    ) : (
                      'None'
                    )}
                  </span>
                </div>
              )}
              <div className="template-detail-page--details-row">
                <span className="template-detail-page--details-label">Created By</span>
                <span className="template-detail-page--details-value">{createdByLabel}</span>
              </div>
              <div className="template-detail-page--details-row">
                <span className="template-detail-page--details-label">Last Updated</span>
                <span className="template-detail-page--details-value">{formatDateOrDash(template.updatedAt)}</span>
              </div>
              {template?.cadenceType !== 'bulk_message' && (
                <>
                  <div className="template-detail-page--details-row">
                    <span className="template-detail-page--details-label">Includes Automated Steps?</span>
                    <span className={`template-detail-page--details-value ${hasAutomated ? 'is-yes' : ''}`}>
                      {hasAutomated ? (
                        <>
                          <Icon icon="check-circle" /> Yes
                        </>
                      ) : (
                        '\u2014'
                      )}
                    </span>
                  </div>
                  <div className="template-detail-page--details-row">
                    <span className="template-detail-page--details-label">Includes Manual Steps?</span>
                    <span className={`template-detail-page--details-value ${hasManual ? 'is-yes' : ''}`}>
                      {hasManual ? (
                        <>
                          <Icon icon="check-circle" /> Yes
                        </>
                      ) : (
                        '\u2014'
                      )}
                    </span>
                  </div>
                </>
              )}
              <div className="template-detail-page--details-row">
                <span className="template-detail-page--details-label">Tags</span>
                <span className="template-detail-page--details-value">
                  {template.tags?.length > 0 ? (
                    <span className="template-detail-page--tags">
                      {template.tags.map((tag) => (
                        <span key={tag} className="template-detail-page--tag">
                          {tag}
                        </span>
                      ))}
                    </span>
                  ) : (
                    '\u2014'
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="template-detail-page--cadences-card">
            <div className="template-detail-page--card-header">
              <h2 className="template-detail-page--card-title">{typeNoun} Using This Blueprint</h2>
              {cadencesUsingTemplate.length > 0 && (
                <button
                  type="button"
                  className="template-detail-page--view-all-btn"
                  onClick={() => {
                    const tab = isBulkBlueprint ? 'bulk' : 'outreach';
                    EverTrue.Navigator(`/cadences-v2?tab=${tab}&blueprint=${templateId}`, { trigger: true });
                  }}
                >
                  <span className="template-detail-page--external-icon">↗</span> View All
                </button>
              )}
            </div>
            {cadencesUsingTemplate.length > 0 ? (
              <div className="template-detail-page--cadences-list">
                {cadencesUsingTemplate.slice(0, 5).map((cadence) =>
                  cadence.canView ? (
                    <div key={cadence.id} className="template-detail-page--cadence-item">
                      <div className="template-detail-page--cadence-info">
                        <button
                          type="button"
                          className="template-detail-page--cadence-name-link"
                          onClick={() => EverTrue.Navigator(`/cadences-v2/${cadence.id}`, { trigger: true })}
                        >
                          {cadence.name}
                        </button>
                        <span className="template-detail-page--cadence-meta">
                          Started {formatDateOrDash(cadence.startedAt || cadence.createdAt)}
                        </span>
                      </div>
                      {renderStatusBadge(cadence.displayStatusLabel)}
                    </div>
                  ) : (
                    <div
                      key={cadence.id}
                      className="template-detail-page--cadence-item template-detail-page--cadence-item-restricted"
                      title="You don't have permission to open this cadence"
                    >
                      <div className="template-detail-page--cadence-info">
                        <span className="template-detail-page--cadence-name-restricted">
                          <Icon icon="lock" size={1} /> {cadence.name}
                        </span>
                        <span className="template-detail-page--cadence-meta">
                          Started {formatDateOrDash(cadence.startedAt || cadence.createdAt)}
                        </span>
                      </div>
                      {renderStatusBadge(cadence.displayStatusLabel)}
                    </div>
                  )
                )}
              </div>
            ) : (
              <p className="template-detail-page--empty-text">
                No {typeNoun.toLowerCase()} have been created from this blueprint yet.
              </p>
            )}
          </div>
        </div>

        {/* Blueprint Steps Table */}
        <div className="template-detail-page--steps-section">
          <div className="template-detail-page--steps-header">
            <h2 className="template-detail-page--card-title">Blueprint Steps</h2>
          </div>
          {stepItems.length > 0 ? (
            <table className="template-detail-page--steps-table">
              <thead>
                <tr>
                  <th>STEP</th>
                  <th>NAME</th>
                  <th>ACTION TYPE</th>
                  <th>MODE</th>
                  <th>TIMING</th>
                  <th>TEMPLATE</th>
                </tr>
              </thead>
              <tbody>
                {stepItems.map((item, idx) => {
                  if (item.type === 'step') return renderStepRow(item);

                  if (item.type === 'branch') {
                    const trueKey = `${idx}_true`;
                    const falseKey = `${idx}_false`;
                    // Only numbered action steps render as rows / step lists; wait
                    // items live in the branch arrays for the flow visualization but
                    // have no num/name here and would otherwise show as "undefined".
                    const trueSteps = getBranchActionSteps(item.trueBranch);
                    const falseSteps = getBranchActionSteps(item.falseBranch);
                    const trueStepList = getBranchStepLabelList(item.trueBranch);
                    const falseStepList = getBranchStepLabelList(item.falseBranch);

                    return (
                      <Fragment key={`branch-${idx}`}>
                        <tr className="template-detail-page--branch-point-row">
                          <td colSpan={6}>
                            <button
                              type="button"
                              className="template-detail-page--branch-toggle"
                              onClick={() => {
                                setExpandedBranches((prev) => {
                                  const allExpanded = prev[trueKey] && prev[falseKey];
                                  return { ...prev, [trueKey]: !allExpanded, [falseKey]: !allExpanded };
                                });
                              }}
                            >
                              <Icon
                                icon={
                                  expandedBranches[trueKey] || expandedBranches[falseKey]
                                    ? 'chevron-down'
                                    : 'chevron-right'
                                }
                              />
                              <span className="template-detail-page--branch-icon">⑂</span>
                              <strong>Branch Point:</strong> {item.conditionName}
                              {item.afterStepNum > 0 ? ` to step ${item.afterStepNum}?` : '?'}
                            </button>
                          </td>
                        </tr>

                        <tr
                          className="template-detail-page--branch-header is-true"
                          aria-expanded={!!expandedBranches[trueKey]}
                        >
                          <td colSpan={6}>
                            <button type="button" onClick={() => toggleBranch(trueKey)}>
                              <Icon icon={expandedBranches[trueKey] ? 'chevron-down' : 'chevron-right'} />
                              <strong>YES</strong> &ndash; {item.condLabels.yesHeader}
                              {trueSteps.length > 0
                                ? `: Continue with steps ${trueStepList}`
                                : ': No steps in this branch'}
                            </button>
                          </td>
                        </tr>
                        {expandedBranches[trueKey] && trueSteps.length > 0 && trueSteps.map(renderStepRow)}
                        {expandedBranches[trueKey] && trueSteps.length === 0 && (
                          <tr className="template-detail-page--branch-step is-true">
                            <td colSpan={6} className="template-detail-page--branch-empty">
                              No steps configured for this branch
                            </td>
                          </tr>
                        )}

                        <tr
                          className="template-detail-page--branch-header is-false"
                          aria-expanded={!!expandedBranches[falseKey]}
                        >
                          <td colSpan={6}>
                            <button type="button" onClick={() => toggleBranch(falseKey)}>
                              <Icon icon={expandedBranches[falseKey] ? 'chevron-down' : 'chevron-right'} />
                              <strong>NO</strong> &ndash; {item.condLabels.noHeader}
                              {falseSteps.length > 0
                                ? `: Continue with steps ${falseStepList}`
                                : ': No steps in this branch'}
                            </button>
                          </td>
                        </tr>
                        {expandedBranches[falseKey] && falseSteps.length > 0 && falseSteps.map(renderStepRow)}
                        {expandedBranches[falseKey] && falseSteps.length === 0 && (
                          <tr className="template-detail-page--branch-step is-false">
                            <td colSpan={6} className="template-detail-page--branch-empty">
                              No steps configured for this branch
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  }

                  return null;
                })}
              </tbody>
            </table>
          ) : (
            <p className="template-detail-page--empty-text">No steps defined in this cadence blueprint.</p>
          )}
        </div>
      </div>

      <DuplicateCadenceModal
        isOpen={actions.showDuplicateModal}
        onClose={() => actions.setShowDuplicateModal(false)}
        onDuplicate={actions.handleDuplicateConfirm}
        entityName={template?.name}
        entityType="template"
      />

      <CadenceSharingModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          refetch();
        }}
        cadenceId={templateId}
        cadenceVersion={template?.version}
        cadenceName={template?.name}
        ownerId={template?.ownerUserId}
      />

      {emailTemplateModal.isOpen && (
        <EmailTemplateFormModal
          template={emailTemplateModal.templateData}
          readOnly={!template.canEdit}
          closeTemplateForm={() => setEmailTemplateModal({ isOpen: false, templateData: null })}
          onSave={template.canEdit ? handleTemplateSave : undefined}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmConfig}
        onClose={() => setConfirmConfig(null)}
        title={confirmConfig?.title || ''}
        body={confirmConfig?.body || ''}
        confirmLabel={confirmConfig?.confirmLabel}
        isDanger={confirmConfig?.isDanger}
        onConfirm={confirmConfig?.onConfirm || (() => {})}
      />
    </div>
  );
};

TemplateDetailPage.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default TemplateDetailPage;
